import { IncomeTile } from "./income-tile";
import NumberHelpers from './../../app/number-helpers';
import { AppSettings } from '../../config/app-settings';
import { useContext, useEffect, useMemo, useState, createContext } from 'react';
import DashboardApi from '../../api/dashboard/dashboard-api-factory';
import { useMsal } from '@azure/msal-react';
import { ExpenseTile } from "./expense-tile";
import { PAndLTile } from "./p-and-l-tile";
import { PaymentAllocationsTile } from "./paymentAllocations-tile";

import TenantApi from "../../api/tenant/tenant-api-factory";
import { SingleStat } from "../reusable-elements/single-stat";
import { BankBalanceCard } from "./bank-balance-card";
import { Link } from 'react-router-dom';

export const IncomeAndExpenseContext = createContext();

export const IncomeAndExpenseContainer = () => {

    const { instance, inProgress, accounts } = useMsal();
    const nh = new NumberHelpers();

    const appSettings = useContext(AppSettings);

    const [shouldFetchData, setShouldFetchData] = useState(true);
    const [data, setData] = useState(null);
    const [fetchingData, setFetchingData] = useState(false);



    const [shouldFetchTenantBalanceData, setShouldFetchTenantBalanceData] = useState(true);
    const [tenantBalanceData, setTenantBalanceData] = useState(null);
    const [fetchingTenantBalanceData, setFetchingTenantBalanceData] = useState(false);

    const dashboardApi = useMemo(() => new DashboardApi(instance, inProgress, accounts, false), [instance, inProgress, accounts]);

    const tenantApi = useMemo(() => new TenantApi(instance, inProgress, accounts, false), [instance, inProgress, accounts]);

    const [fetchErrors, setFetchErrors] = useState({
        transactions: null,
        tenantBalance: null
    });


    const chartSettings = {
        options: {
            chart: {
                type: 'bar',
                width: "150%",
                height: 75,
                sparkline: {
                    enabled: true
                },
                stacked: true
            },
            stroke: {
                curve: 'smooth',
                width: 3
            },
            fill: {
                type: 'gradient',
                gradient: {
                    opacityFrom: 1,
                    opacityTo: 1,
                    colorStops: [{
                        offset: 0,
                        color: appSettings.color.blue,
                        opacity: 1
                    },
                    {
                        offset: 100,
                        color: appSettings.color.indigo,
                        opacity: 1
                    }]
                },
            },
            tooltip: {
                theme: 'dark',
                fixed: {
                    enabled: false
                },
                x: {
                    show: false
                },
                y: {
                    title: {
                        formatter: function (seriesName) {
                            return ''
                        }
                    },
                    formatter: (value) => {
                        return nh.convertNumberWithCommas(value, "£")
                    },
                },
                marker: {
                    show: false
                }
            },
            responsive: [{
                breakpoint: 1500,
                options: {
                    chart: {
                        width: 130
                    }
                }
            }, {
                breakpoint: 1300,
                options: {
                    chart: {
                        width: 100
                    }
                }
            }, {
                breakpoint: 1200,
                options: {
                    chart: {
                        width: 200
                    }
                }
            }, {
                breakpoint: 576,
                options: {
                    chart: {
                        width: 180
                    }
                }
            }, {
                breakpoint: 400,
                options: {
                    chart: {
                        width: 120
                    }
                }
            }]
        }
    };


    useEffect(() => {
        async function fetchData() {
            if (shouldFetchData && !fetchingData) {
                setFetchingData(true);
                // Reset only the specific error
                setFetchErrors(prev => ({ ...prev, transactions: null }));
                try {
                    const data = await dashboardApi.getPeriodicBankTransactions();
                    setData(data);
                } catch (error) {
                    // Check for authorization errors (typically 401 or 403)
                    if (error.response && (error.response.status === 401 || error.response.status === 403)) {
                        setFetchErrors(prev => ({
                            ...prev,
                            transactions: "You don't have permission to access this data"
                        }));
                    } else {
                        setFetchErrors(prev => ({
                            ...prev,
                            transactions: "Failed to fetch transaction data"
                        }));
                        console.error("Error fetching periodic bank transactions:", error);
                    }
                } finally {
                    setShouldFetchData(false);
                    setFetchingData(false);
                }
            }
        }
        fetchData();
    }, [dashboardApi, fetchingData, shouldFetchData]);



    useEffect(() => {
        async function fetchData() {
            if (shouldFetchTenantBalanceData && !fetchingTenantBalanceData) {
                setFetchingTenantBalanceData(true);
                // Reset only the specific error
                setFetchErrors(prev => ({ ...prev, tenantBalance: null }));
                try {
                    const data = await tenantApi.getOverallBalance();
                    setTenantBalanceData(data);
                } catch (error) {
                    // Check for authorization errors
                    if (error.response && (error.response.status === 401 || error.response.status === 403)) {
                        setFetchErrors(prev => ({
                            ...prev,
                            tenantBalance: "You don't have permission to access tenant balance data"
                        }));
                    } else {
                        setFetchErrors(prev => ({
                            ...prev,
                            tenantBalance: "Failed to fetch tenant balance data"
                        }));
                        console.error("Error fetching tenant balance data:", error);
                    }
                } finally {
                    setShouldFetchTenantBalanceData(false);
                    setFetchingTenantBalanceData(false);
                }
            }
        }
        fetchData();
    }, [tenantApi, fetchingTenantBalanceData, shouldFetchTenantBalanceData]);

    return (
        <IncomeAndExpenseContext.Provider value={{ data, fetchingData, chartSettings, fetchErrors }}>
                <div className="row">
				<div className="col-xl-6 mb-xl-0">
                    <BankBalanceCard />
                </div>
                <div className="col-xl-6">
                    <Link to="/tenant/search?balance=true&view=list" className="text-decoration-none">
                        <SingleStat
                            headerTitle="TOTAL TENANT DEBT"
                            summaryValue={`£${tenantBalanceData && tenantBalanceData.balance ? tenantBalanceData.balance.toFixed(2) : "0.00"}`}
                            summaryValueColour={(!!tenantBalanceData) && (tenantBalanceData.balance !== undefined) && parseInt(tenantBalanceData.balance.toFixed(2)) >= 0 ? "text-green-500" : "text-red-500"}
                            loading={fetchingTenantBalanceData}
                            complete={!!!shouldFetchTenantBalanceData}
                            error={fetchErrors.tenantBalance}
                        />
                    </Link>
                </div>
                <div className="col-xl-6">
                    <IncomeTile />
                </div>
                <div className="col-xl-6">
                    <ExpenseTile />
                </div>
                <div className="col-xl-6">
                    <PaymentAllocationsTile />
                </div>
                <div className="col-xl-6">
                    <PAndLTile />
                </div>
            </div>
        </IncomeAndExpenseContext.Provider>
    );
};